import React, { useEffect, useState } from "react";
import { ContextMenuControl, IconControl } from "spark-core-dx/components";
import "./DataHeader.scss";

const DATA_STATUS_MENU_ESTIMATED_WIDTH = 160;
const DATA_STATUS_MENU_RIGHT_NUDGE = -16;

const DataHeader = ({
  title = "Data Models",
  description = "",
  subtitle = "",
  iconKey = "schema",
  statusMenus = [],
  statusFooter = null,
  leftFooter = null,
  headerActions = null,
  onBackClick = null,
  backButtonTitle = "Back",
}) => {
  const [openStatusMenuKey, setOpenStatusMenuKey] = useState(false);
  const [statusMenuStyle, setStatusMenuStyle] = useState(null);

  useEffect(() => {
    setOpenStatusMenuKey(false);
    setStatusMenuStyle(null);
  }, [title]);

  const hasStatusMenus = Array.isArray(statusMenus) && statusMenus.length > 0;
  const activeStatusMenu = hasStatusMenus
    ? statusMenus.find((menu) => String(menu?.key ?? "") === String(openStatusMenuKey ?? "")) ?? null
    : null;

  const handleOpenStatusMenu = (event, key) => {
    if (!key) {
      return;
    }

    const nextKey = String(openStatusMenuKey ?? "") === String(key) ? false : key;
    const anchorElement = event?.currentTarget?.querySelector?.(".data-header__chip") ?? event?.currentTarget;
    const rect = anchorElement?.getBoundingClientRect?.();
    if (rect) {
      const maxLeft = Math.max(16, window.innerWidth - DATA_STATUS_MENU_ESTIMATED_WIDTH - 16);
      const nextLeft = Math.min(
        Math.max(16, rect.right - DATA_STATUS_MENU_ESTIMATED_WIDTH + DATA_STATUS_MENU_RIGHT_NUDGE),
        maxLeft
      );
      setStatusMenuStyle({
        left: nextLeft,
        top: rect.bottom + 6,
      });
    }
    setOpenStatusMenuKey(nextKey);
  };

  const renderStatusMenuOptions = (options = []) => (
    <div className="options-wrapper data-header-status-options">
      {options.map((option) => (
        <div
          key={`${option?.text ?? "option"}-${option?.icon ?? "icon"}`}
          className={`option${option?.disabled ? " disabled" : ""}${option?.danger ? " danger" : ""}`}
          onClick={option?.disabled
            ? undefined
            : () => {
              setOpenStatusMenuKey(false);
              option?.onClick?.();
            }}
        >
          {option?.icon ? <IconControl iconKey={option?.icon} /> : null}
          <span className="data-header-status-option__text">{option?.text}</span>
        </div>
      ))}
    </div>
  );

  return (
    <div className="data-header">
      <div className="data-header__top">
        <div className="data-header__left">
          <IconControl
            className="data-header__icon"
            iconKey={iconKey}
            color={"var(--primary-text)"}
          />
          <div className="data-header__title">
            {title}
          </div>
        </div>
        <div className="data-header__right">
          {subtitle ? (
            <span className="data-header__subtitle">
              {subtitle}
            </span>
          ) : null}
          {headerActions}
          {typeof onBackClick === "function" ? (
            <button
              type="button"
              className="data-header__back-button"
              title={backButtonTitle}
              aria-label={backButtonTitle}
              onClick={onBackClick}
            >
              <IconControl iconKey="undo" />
            </button>
          ) : null}
        </div>
      </div>

      <div className="data-header__bottom">
        <div className="data-header__left">
          <div className="data-header__left-stack">
            <div className={`data-header__description${!description ? " is-empty" : ""}`}>
              {description}
            </div>
            {leftFooter ? (
              <div className="data-header__left-footer">
                {leftFooter}
              </div>
            ) : null}
          </div>
        </div>
        <div className="data-header__right">
          <div className="data-header__status-container">
            <div className="data-header__status-panel">
            {statusMenus.map((menu) => {
              const menuKey = String(menu?.key ?? "");
              const isMenuClickable = menuKey.length > 0;
              const showCaret = (menu?.showCaret !== false) && isMenuClickable;
              const detailTone = menu?.detailTone ?? menu?.tone ?? "";
              const labelTone = menu?.labelTone ?? menu?.tone ?? "";

              return (
                <div
                  key={menuKey || menu?.label || menu?.detail}
                  className={`data-header__status-row${isMenuClickable ? " is-clickable" : ""}`}
                  role={isMenuClickable ? "button" : undefined}
                  tabIndex={isMenuClickable ? 0 : undefined}
                  title={menu?.title || menu?.label || menu?.detail || undefined}
                  onClick={isMenuClickable ? (event) => handleOpenStatusMenu(event, menuKey) : undefined}
                  onKeyDown={isMenuClickable
                    ? (event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        handleOpenStatusMenu(event, menuKey);
                      }
                    }
                    : undefined}
                >
                  {menu?.detail ? (
                    <span className={`data-header__status-detail${detailTone ? ` data-header__status-detail--${detailTone}` : ""}${menu?.detailClassName ? ` ${menu.detailClassName}` : ""}`}>
                      {menu.detail}
                    </span>
                  ) : null}
                  {menu?.label ? (
                    <span className={`data-header__chip${labelTone ? ` data-header__chip--${labelTone}` : ""}${menu?.labelClassName ? ` ${menu.labelClassName}` : ""}`}>
                      <span className="data-header__chip-text">{menu.label}</span>
                      {showCaret ? (
                        <IconControl className="data-header__chip-caret" iconKey="arrowdropdown" />
                      ) : null}
                    </span>
                  ) : null}
                </div>
              );
            })}

            {activeStatusMenu ? (
              <ContextMenuControl
                openContextMenu={openStatusMenuKey}
                setOpenContextMenu={setOpenStatusMenuKey}
                contextMenuStyle={statusMenuStyle}
              >
                {renderStatusMenuOptions(activeStatusMenu?.items)}
              </ContextMenuControl>
            ) : null}
            </div>
            {statusFooter ? (
              <div className="data-header__status-footer">
                {statusFooter}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataHeader;
